import MessageDraftsAPI from 'dashboard/api/messageDrafts';
import types from '../mutation-types';

let uploadIdCounter = 0;

const uploadQueues = {};

const getQueue = draftId => {
  if (!uploadQueues[draftId]) {
    uploadQueues[draftId] = {
      items: [],
      processing: false,
      activeAbort: null,
      activeTempId: null,
    };
  }
  return uploadQueues[draftId];
};

const state = {
  records: {},
  uploadingFiles: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isUploadingFile: false,
  },
};

export const getters = {
  getDraft: _state => draftId => {
    return _state.records[draftId] || null;
  },
  getAllDrafts: _state => {
    return Object.values(_state.records);
  },
  getUploadingFiles: _state => draftId => {
    return _state.uploadingFiles[draftId] || [];
  },
  getUIFlags: _state => _state.uiFlags,
};

function mapDraftFromApi(data) {
  return {
    id: data.id,
    publicContent: data.public_content,
    privateContent: data.private_content,
    publicFiles: data.public_files || [],
    privateFiles: data.private_files || [],
    ccEmails: data.cc_emails || [],
    bccEmails: data.bcc_emails || [],
    toEmails: data.to_emails || [],
    contactId: data.contact_id,
    inboxId: data.inbox_id,
    subject: data.subject,
    fromEmail: data.from_email,
    hasPublicContent: data.has_public_content || false,
    hasPrivateContent: data.has_private_content || false,
    sourceMessageId: data.source_message_id,
  };
}

export const actions = {
  fetchAll: async ({ commit }) => {
    commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isFetching: true });
    try {
      const { data } = await MessageDraftsAPI.index();
      const records = {};
      data.forEach(draft => {
        records[draft.id] = mapDraftFromApi(draft);
      });
      commit(types.SET_NEW_CONV_DRAFTS, records);
    } catch {
      // noop
    } finally {
      commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, params = {}) => {
    commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isCreating: true });
    try {
      const { data } = await MessageDraftsAPI.create(params);
      const draft = mapDraftFromApi(data);
      commit(types.SET_NEW_CONV_DRAFT, { draftId: draft.id, draft });
      return draft;
    } finally {
      commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isCreating: false });
    }
  },

  forward: async ({ commit }, messageId) => {
    commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isCreating: true });
    try {
      const { data } = await MessageDraftsAPI.forward(messageId);
      const draft = mapDraftFromApi(data);
      commit(types.SET_NEW_CONV_DRAFT, { draftId: draft.id, draft });
      return draft;
    } finally {
      commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isCreating: false });
    }
  },

  fetch: async ({ commit }, draftId) => {
    commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isFetching: true });
    try {
      const { data } = await MessageDraftsAPI.show(draftId);
      const draft = mapDraftFromApi(data);
      commit(types.SET_NEW_CONV_DRAFT, { draftId: draft.id, draft });
    } catch {
      // noop
    } finally {
      commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isFetching: false });
    }
  },

  update: async ({ commit, state: _state }, { draftId, ...params }) => {
    const existing = _state.records[draftId];
    if (existing) {
      const updated = { ...existing };
      if (params.publicContent !== undefined)
        updated.publicContent = params.publicContent;
      if (params.privateContent !== undefined)
        updated.privateContent = params.privateContent;
      if (params.contactId !== undefined) updated.contactId = params.contactId;
      if (params.inboxId !== undefined) updated.inboxId = params.inboxId;
      if (params.subject !== undefined) updated.subject = params.subject;
      if (params.ccEmails !== undefined) updated.ccEmails = params.ccEmails;
      if (params.bccEmails !== undefined) updated.bccEmails = params.bccEmails;
      if (params.toEmails !== undefined) updated.toEmails = params.toEmails;
      if (params.fromEmail !== undefined) updated.fromEmail = params.fromEmail;
      commit(types.SET_NEW_CONV_DRAFT, { draftId, draft: updated });
    }

    commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await MessageDraftsAPI.update(draftId, params);
      const draft = mapDraftFromApi(data);
      commit(types.SET_NEW_CONV_DRAFT, { draftId: draft.id, draft });
    } catch {
      // noop
    } finally {
      commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isUpdating: false });
    }
  },

  destroy: async ({ commit }, draftId) => {
    try {
      await MessageDraftsAPI.destroy(draftId);
    } catch {
      // noop
    }
    commit(types.REMOVE_NEW_CONV_DRAFT, { draftId });
  },

  addAttachment: (
    { commit, dispatch },
    { draftId, file, fileMeta, isPrivate }
  ) => {
    uploadIdCounter += 1;
    const tempId = `uploading-${uploadIdCounter}`;
    const queue = getQueue(draftId);

    queue.items.push({ tempId, file, fileMeta, isPrivate });

    commit(types.ADD_NEW_CONV_DRAFT_UPLOADING_FILE, {
      draftId,
      file: {
        tempId,
        filename: fileMeta?.name || 'file',
        byte_size: fileMeta?.size || 0,
        content_type: fileMeta?.type || 'application/octet-stream',
        status: 'queued',
        isPrivate,
      },
    });

    dispatch('processUploadQueue', draftId);
  },

  processUploadQueue: async ({ commit, state: _state }, draftId) => {
    const queue = getQueue(draftId);
    if (queue.processing) return;
    queue.processing = true;

    while (queue.items.length > 0) {
      const item = queue.items.shift();
      const abortController = new AbortController();
      queue.activeAbort = abortController;
      queue.activeTempId = item.tempId;

      commit(types.SET_NEW_CONV_DRAFT_UPLOADING_FILE_STATUS, {
        draftId,
        tempId: item.tempId,
        status: 'uploading',
      });

      commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isUploadingFile: true });
      try {
        const apiCall =
          typeof item.file === 'string'
            ? MessageDraftsAPI.addAttachmentBlob
            : MessageDraftsAPI.addAttachmentFile;
        // eslint-disable-next-line no-await-in-loop
        const { data } = await apiCall.call(
          MessageDraftsAPI,
          draftId,
          item.file,
          { signal: abortController.signal, isPrivate: item.isPrivate }
        );
        const existing = _state.records[draftId] || {};
        commit(types.SET_NEW_CONV_DRAFT, {
          draftId,
          draft: {
            ...existing,
            publicFiles: data.public_files || [],
            privateFiles: data.private_files || [],
          },
        });
      } catch {
        // noop
      } finally {
        commit(types.REMOVE_NEW_CONV_DRAFT_UPLOADING_FILE, {
          draftId,
          tempId: item.tempId,
        });
        queue.activeAbort = null;
        queue.activeTempId = null;
      }
    }

    queue.processing = false;
    commit(types.SET_NEW_CONV_DRAFT_UI_FLAG, { isUploadingFile: false });
  },

  cancelUpload: ({ commit }, { draftId, tempId }) => {
    const queue = getQueue(draftId);

    if (queue.activeTempId === tempId && queue.activeAbort) {
      queue.activeAbort.abort();
    } else {
      queue.items = queue.items.filter(item => item.tempId !== tempId);
    }

    commit(types.REMOVE_NEW_CONV_DRAFT_UPLOADING_FILE, {
      draftId,
      tempId,
    });
  },

  removeAttachment: async ({ commit }, { draftId, attachmentId }) => {
    commit(types.REMOVE_NEW_CONV_DRAFT_FILE, { draftId, attachmentId });
    try {
      await MessageDraftsAPI.removeAttachment(draftId, attachmentId);
    } catch {
      const { data } = await MessageDraftsAPI.show(draftId);
      const draft = mapDraftFromApi(data);
      commit(types.SET_NEW_CONV_DRAFT, { draftId: draft.id, draft });
    }
  },
};

export const mutations = {
  [types.SET_NEW_CONV_DRAFTS]($state, records) {
    $state.records = records;
  },
  [types.SET_NEW_CONV_DRAFT]($state, { draftId, draft }) {
    $state.records = {
      ...$state.records,
      [draftId]: draft,
    };
  },
  [types.REMOVE_NEW_CONV_DRAFT]($state, { draftId }) {
    const { [draftId]: _, ...rest } = $state.records;
    $state.records = rest;
  },
  [types.SET_NEW_CONV_DRAFT_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.REMOVE_NEW_CONV_DRAFT_FILE]($state, { draftId, attachmentId }) {
    const existing = $state.records[draftId];
    if (existing) {
      $state.records = {
        ...$state.records,
        [draftId]: {
          ...existing,
          publicFiles: existing.publicFiles.filter(f => f.id !== attachmentId),
          privateFiles: existing.privateFiles.filter(
            f => f.id !== attachmentId
          ),
        },
      };
    }
  },
  [types.ADD_NEW_CONV_DRAFT_UPLOADING_FILE]($state, { draftId, file }) {
    $state.uploadingFiles = {
      ...$state.uploadingFiles,
      [draftId]: [...($state.uploadingFiles[draftId] || []), file],
    };
  },
  [types.REMOVE_NEW_CONV_DRAFT_UPLOADING_FILE]($state, { draftId, tempId }) {
    const existing = $state.uploadingFiles[draftId] || [];
    const filtered = existing.filter(f => f.tempId !== tempId);
    $state.uploadingFiles = {
      ...$state.uploadingFiles,
      [draftId]: filtered,
    };
  },
  [types.SET_NEW_CONV_DRAFT_UPLOADING_FILE_STATUS](
    $state,
    { draftId, tempId, status }
  ) {
    const existing = $state.uploadingFiles[draftId] || [];
    $state.uploadingFiles = {
      ...$state.uploadingFiles,
      [draftId]: existing.map(f =>
        f.tempId === tempId ? { ...f, status } : f
      ),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
